// src/pages/GuestCheckout.js - ADD PROCESSING SCREEN
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  CreditCard,
  MapPin,
  User,
  Package,
  ArrowLeft,
  Lock,
  Tag,
  X,
  Mail,
  Phone,
} from "lucide-react";
import { useGuest } from "../context/GuestContext";
import { guestAPI, pincodeAPI, promoCodeAPI } from "../services/api";
import { useRazorpay } from "../hooks/useRazorpay";
import Button from "../components/common/Button";
import Loading from "../components/common/Loading";
import BackgroundWrapper from "../components/common/BackgroundWrapper";
import PincodeInput from "../components/common/PincodeInput";
import CityInput from "../components/common/CityInput";
import ServiceAreaNotice from "../components/common/ServiceAreaNotice";
import PaymentProcessing from "../components/common/PaymentProcessing";
import toast from "react-hot-toast";
import "./Checkout.css";

const GuestCheckout = () => {
  const navigate = useNavigate();
  const {
    guestItems,
    guestSubtotal,
    loading: guestLoading,
    clearGuestCart,
  } = useGuest();
  const { processPayment, loading: paymentLoading } = useRazorpay();

  const [processing, setProcessing] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [pincodeAutofilled, setPincodeAutofilled] = useState(false);
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [showProcessingScreen, setShowProcessingScreen] = useState(false);

  const [paymentSettings, setPaymentSettings] = useState({
    razorpayEnabled: true,
    codEnabled: true,
  });

  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [showPromoInput, setShowPromoInput] = useState(false);

  const [guestInfo, setGuestInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "India",
  });

  const [paymentData, setPaymentData] = useState({
    method: "razorpay",
  });

  // ✅ ADD THIS: Set up global function for processing screen
  useEffect(() => {
    window.__showProcessingScreen = () => {
      console.log("⚡ Showing processing screen for guest checkout");
      setShowProcessingScreen(true);
    };
    return () => {
      window.__showProcessingScreen = null;
    };
  }, []);

  useEffect(() => {
    if (orderPlaced) return;
    if (!guestItems || guestItems.length === 0) {
      toast.error("Your cart is empty");
      navigate("/cart");
    }
  }, [guestItems, navigate, orderPlaced]);

  useEffect(() => {
    loadPaymentSettings();
  }, []);

  const loadPaymentSettings = async () => {
    try {
      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL || "http://localhost:3001/api"
        }/payment-settings`
      );
      const data = await response.json();

      if (data.success) {
        setPaymentSettings({
          razorpayEnabled: data.data.razorpayEnabled,
          codEnabled: data.data.codEnabled,
        });

        if (paymentData.method === "razorpay" && !data.data.razorpayEnabled) {
          if (data.data.codEnabled) {
            setPaymentData({ method: "cash_on_delivery" });
          }
        }
      }
    } catch (error) {
      console.error("Failed to load payment settings:", error);
    }
  };

  const calculateTotals = () => {
    const subtotal = guestSubtotal || 0;
    const discount = appliedPromo?.discountAmount || 0;
    const subtotalAfterDiscount = Math.max(0, subtotal - discount);
    const shipping = deliveryInfo?.deliveryCharge ?? null;

    if (!deliveryInfo) {
      return {
        subtotal,
        discount,
        shipping: null,
        tax: 0,
        total: subtotal,
      };
    }

    const tax = Math.round(subtotalAfterDiscount * 0.18);

    return {
      subtotal,
      discount,
      shipping,
      tax,
      total: subtotalAfterDiscount + shipping + tax,
    };
  };

  const totals = calculateTotals();

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) {
      toast.error("Please enter a promo code");
      return;
    }

    try {
      setPromoLoading(true);

      const { subtotal } = calculateTotals();

      const itemsForValidation = guestItems.map((item) => ({
        productId: item.product?._id,
        categoryId: item.product?.category,
        quantity: item.quantity,
      }));

      const response = await promoCodeAPI.validatePromoCode({
        code: promoCode.trim().toUpperCase(),
        orderAmount: subtotal,
        items: itemsForValidation,
      });

      if (response.data.success) {
        setAppliedPromo({
          code: response.data.promoCode.code,
          description: response.data.promoCode.description,
          discountAmount: response.data.discount.amount,
          discountType: response.data.discount.type,
        });

        toast.success(`🎉 ${response.data.message}`);
        setShowPromoInput(false);
      }
    } catch (error) {
      console.error("Promo code error:", error);
      toast.error(error.response?.data?.message || "Invalid promo code");
      setAppliedPromo(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoCode("");
    toast.success("Promo code removed");
  };

  const validateGuestInfo = () => {
    const errors = [];

    if (!guestInfo.firstName.trim()) errors.push("First name is required");
    if (!guestInfo.lastName.trim()) errors.push("Last name is required");

    const emailRegex = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
    if (!guestInfo.email.trim()) {
      errors.push("Email is required");
    } else if (!emailRegex.test(guestInfo.email)) {
      errors.push("Please enter a valid email address");
    }

    const phoneRegex = /^[6-9]\d{9}$/;
    if (!guestInfo.phone.trim()) {
      errors.push("Phone number is required");
    } else if (!phoneRegex.test(guestInfo.phone.replace(/\D/g, ""))) {
      errors.push("Please enter a valid 10-digit mobile number");
    }

    if (!guestInfo.address.trim()) errors.push("Address is required");
    if (!guestInfo.city.trim()) errors.push("City is required");
    if (!guestInfo.state.trim()) errors.push("State is required");

    if (!guestInfo.zipCode.trim()) {
      errors.push("PIN code is required");
    } else if (!/^\d{6}$/.test(guestInfo.zipCode)) {
      errors.push("PIN code must be 6 digits");
    }

    if (errors.length > 0) {
      errors.forEach((error) => toast.error(error));
      return false;
    }

    return true;
  };

  const handleInputChange = (field, value) => {
    setGuestInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePincodeAutofill = (locationData) => {
    if (!locationData) return;

    setGuestInfo((prev) => ({
      ...prev,
      city: locationData.city,
      state: locationData.state,
    }));

    const rawCharge =
      locationData.deliveryCharge ?? locationData.charge ?? null;

    const normalizedCharge =
      rawCharge === null
        ? null
        : rawCharge === 0 || rawCharge === "Free"
        ? 0
        : Number(rawCharge);

    setDeliveryInfo({
      deliveryCharge: normalizedCharge,
      state: locationData.state,
      estimatedDays: locationData.estimatedDays ?? 3,
    });

    setPincodeAutofilled(true);

    const chargeText =
      normalizedCharge === 0
        ? "Free delivery"
        : `₹${normalizedCharge} delivery charge`;

    toast.success(`${chargeText} to ${locationData.state}`);
  };

  const placeOrder = async () => {
    if (!validateGuestInfo()) {
      return;
    }

    if (!guestItems || guestItems.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    try {
      setProcessing(true);

      const orderData = {
        email: guestInfo.email,
        firstName: guestInfo.firstName,
        lastName: guestInfo.lastName,
        phone: guestInfo.phone,
        shippingAddress: {
          firstName: guestInfo.firstName,
          lastName: guestInfo.lastName,
          addressLine1: guestInfo.address,
          city: guestInfo.city,
          state: guestInfo.state,
          postalCode: guestInfo.zipCode,
          country: "India",
        },
        billingAddress: {
          firstName: guestInfo.firstName,
          lastName: guestInfo.lastName,
          addressLine1: guestInfo.address,
          city: guestInfo.city,
          state: guestInfo.state,
          postalCode: guestInfo.zipCode,
          country: "India",
        },
        paymentMethod: paymentData.method === "razorpay" ? "razorpay" : "cod",
        items: guestItems.map((item) => ({
          product: item.product._id,
          size: item.variant.size,
          color: item.variant.color.name,
          quantity: item.quantity,
        })),
        promoCode: appliedPromo?.code || null,
        shipping: totals.shipping,
        discount: totals.discount,
        specialInstructions: guestInfo.specialInstructions || "",
      };

      if (paymentData.method === "razorpay") {
        const flattenedItems = guestItems.map((item) => ({
          productId: item.product._id,
          product: item.product._id,
          size: item.variant.size,
          color: item.variant.color.name,
          quantity: item.quantity,
          price:
            item.priceAtAdd || item.product.salePrice || item.product.price,
        }));

        processPayment(
          {
            ...orderData,
            items: flattenedItems,
            shipping: totals.shipping,
            tax: totals.tax,
            discount: totals.discount,
          },
          async (verifiedOrder) => {
            toast.success("Order placed successfully!");
            setOrderPlaced(true);
            clearGuestCart();

            const trackingToken = verifiedOrder.trackingToken;
            if (trackingToken) {
              navigate(`/track-order/${trackingToken}`, { replace: true });
            } else {
              navigate(`/order-success/${verifiedOrder._id}`, {
                replace: true,
              });
            }
          },
          (error) => {
            console.error("❌ Payment failed:", error);
            toast.error(error.message || "Payment failed. Please try again.");
            setProcessing(false);
          }
        );
      } else if (paymentData.method === "cod") {
        const response = await guestAPI.guestCheckout(orderData);

        if (response.data.success) {
          toast.success("Order placed successfully!");
          setOrderPlaced(true);
          clearGuestCart();

          navigate(`/track-order/${response.data.order.trackingToken}`, {
            replace: true,
          });
        }
      }
    } catch (error) {
      console.error("Order placement error:", error);

      const data = error.response?.data;

      if (data?.whatsappLink) {
        toast.custom(() => (
          <div style={{ padding: "12px", maxWidth: "320px" }}>
            <p style={{ marginBottom: "10px" }}>{data.message}</p>
            <a
              href={data.whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-block",
                padding: "10px 14px",
                background: "#25D366",
                color: "#fff",
                borderRadius: "6px",
                textDecoration: "none",
                fontWeight: 500,
              }}
            >
              💬 Order via WhatsApp
            </a>
          </div>
        ));
        setProcessing(false);
        return;
      }

      toast.error(data?.message || "Failed to place order");
      setProcessing(false);
    }
  };

  if (guestLoading) {
    return <Loading size="lg" text="Loading checkout..." fullScreen />;
  }

  //Show processing screen during payment verification
  if (showProcessingScreen) {
    return <PaymentProcessing message="Processing your guest order..." />;
  }

  return (
    <BackgroundWrapper>
      <div className="checkout-page">
        <div className="container">
          <div className="checkout-header">
            <button onClick={() => navigate("/cart")} className="back-btn">
              <ArrowLeft size={20} />
              Back to Cart
            </button>
            <h1>Guest Checkout</h1>
            <div className="security-badge">
              <Lock size={16} />
              Secure Checkout
            </div>
          </div>

          <ServiceAreaNotice />

          <div className="checkout-content">
            <div className="checkout-main">
              <div className="checkout-section">
                <div className="section-header">
                  <User size={24} />
                  <h2>Your Information</h2>
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label>
                      First Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={guestInfo.firstName}
                      onChange={(e) =>
                        handleInputChange("firstName", e.target.value)
                      }
                      placeholder="Enter your first name"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Last Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={guestInfo.lastName}
                      onChange={(e) =>
                        handleInputChange("lastName", e.target.value)
                      }
                      placeholder="Enter your last name"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Email Address <span className="required">*</span>
                    </label>
                    <div className="input-with-icon">
                      <Mail size={18} />
                      <input
                        type="email"
                        value={guestInfo.email}
                        onChange={(e) =>
                          handleInputChange("email", e.target.value)
                        }
                        placeholder="your.email@example.com"
                        required
                      />
                    </div>
                    <p className="field-note">
                      We'll send your order confirmation here
                    </p>
                  </div>

                  <div className="form-group">
                    <label>
                      Phone Number <span className="required">*</span>
                    </label>
                    <div className="input-with-icon">
                      <Phone size={18} />
                      <input
                        type="tel"
                        value={guestInfo.phone}
                        onChange={(e) =>
                          handleInputChange("phone", e.target.value)
                        }
                        placeholder="10-digit mobile number"
                        maxLength="10"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="checkout-section">
                <div className="section-header">
                  <MapPin size={24} />
                  <h2>Delivery Address</h2>
                </div>

                <div className="form-group full-width">
                  <label>
                    Address <span className="required">*</span>
                  </label>
                  <textarea
                    value={guestInfo.address}
                    onChange={(e) =>
                      handleInputChange("address", e.target.value)
                    }
                    placeholder="House no., Building name, Street"
                    rows="3"
                    required
                  />
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label>
                      PIN Code <span className="required">*</span>
                    </label>
                    <PincodeInput
                      value={guestInfo.zipCode}
                      onChange={(pincode) => {
                        handleInputChange("zipCode", pincode);

                        if (!/^[1-9][0-9]{5}$/.test(pincode)) {
                          setPincodeAutofilled(false);
                          setDeliveryInfo(null);
                        }
                      }}
                      onAutofill={handlePincodeAutofill}
                      showDeliveryInfo={true}
                      placeholder="6-digit PIN code"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      City/District <span className="required">*</span>
                    </label>
                    {pincodeAutofilled ? (
                      <div className="autofilled-field">
                        <input
                          type="text"
                          value={guestInfo.city}
                          disabled
                          className="autofilled-input"
                        />
                        <small className="autofilled-label">
                          ✓ Auto-filled from PIN code
                        </small>
                      </div>
                    ) : (
                      <CityInput
                        value={guestInfo.city}
                        onChange={(city) => handleInputChange("city", city)}
                        placeholder="Enter city"
                        required
                      />
                    )}
                  </div>

                  <div className="form-group">
                    <label>
                      State <span className="required">*</span>
                    </label>
                    {pincodeAutofilled ? (
                      <div className="autofilled-field">
                        <input
                          type="text"
                          value={guestInfo.state}
                          disabled
                          className="autofilled-input"
                        />
                        <small className="autofilled-label">
                          ✓ Auto-filled from PIN code
                        </small>
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={guestInfo.state}
                        onChange={(e) =>
                          handleInputChange("state", e.target.value)
                        }
                        placeholder="Enter state"
                        required
                      />
                    )}
                  </div>

                  <div className="form-group">
                    <label>Country</label>
                    <input
                      type="text"
                      value="India"
                      disabled
                      className="disabled-input"
                    />
                  </div>
                </div>
              </div>

              <div className="checkout-section">
                <div className="section-header">
                  <CreditCard size={24} />
                  <h2>Payment Method</h2>
                </div>

                <div className="payment-methods">
                  {paymentSettings.razorpayEnabled && (
                    <label className="payment-method">
                      <input
                        type="radio"
                        name="payment"
                        value="razorpay"
                        checked={paymentData.method === "razorpay"}
                        onChange={() => setPaymentData({ method: "razorpay" })}
                      />
                      <div className="method-details">
                        <h4>Online Payment (Razorpay)</h4>
                        <p>Pay securely with UPI, Cards, or Net Banking</p>
                      </div>
                      <CreditCard size={20} />
                    </label>
                  )}

                  {paymentSettings.codEnabled && (
                    <label className="payment-method">
                      <input
                        type="radio"
                        name="payment"
                        value="cash_on_delivery"
                        checked={paymentData.method === "cash_on_delivery"}
                        onChange={() =>
                          setPaymentData({ method: "cash_on_delivery" })
                        }
                      />
                      <div className="method-details">
                        <h4>Cash on Delivery</h4>
                        <p>Pay when you receive your order</p>
                      </div>
                      <Package size={20} />
                    </label>
                  )}
                </div>

                <div className="promo-section">
                  {!appliedPromo ? (
                    <>
                      {!showPromoInput ? (
                        <button
                          type="button"
                          className="promo-toggle-btn"
                          onClick={() => setShowPromoInput(true)}
                        >
                          <Tag size={18} />
                          Have a promo code?
                        </button>
                      ) : (
                        <div className="promo-input-group">
                          <input
                            type="text"
                            placeholder="Enter promo code"
                            value={promoCode}
                            onChange={(e) =>
                              setPromoCode(e.target.value.toUpperCase())
                            }
                            disabled={promoLoading}
                          />
                          <Button
                            variant="outline"
                            onClick={handleApplyPromo}
                            loading={promoLoading}
                            disabled={promoLoading || !promoCode.trim()}
                          >
                            Apply
                          </Button>
                          <button
                            type="button"
                            className="promo-cancel-btn"
                            onClick={() => {
                              setShowPromoInput(false);
                              setPromoCode("");
                            }}
                            disabled={promoLoading}
                          >
                            <X size={16} />
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="applied-promo">
                      <div className="promo-info">
                        <Tag size={18} className="promo-icon" />
                        <div>
                          <p className="promo-code">{appliedPromo.code}</p>
                          <p className="promo-description">
                            {appliedPromo.description}
                          </p>
                          <p className="promo-savings">
                            You saved ₹{appliedPromo.discountAmount.toFixed(2)}!
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="remove-promo-btn"
                        onClick={handleRemovePromo}
                      >
                        <X size={18} />
                      </button>
                    </div>
                  )}
                </div>

                <div className="policy-notice">
                  <p>
                    <strong>📋 Cancellation Policy:</strong>{" "}
                    {paymentData.method === "razorpay"
                      ? "You can cancel within 24 hours of order placement for an automatic refund."
                      : "You can cancel this order anytime before it is shipped."}
                  </p>
                </div>

                <div className="final-actions">
                  <Button
                    onClick={placeOrder}
                    loading={processing}
                    disabled={processing}
                    className="place-order-btn"
                  >
                    <Lock size={18} />
                    {processing
                      ? "Processing..."
                      : `Place Order - ₹${totals.total.toFixed(2)}`}
                  </Button>

                  <p className="guest-account-note">
                    Want to track your orders easily?{" "}
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => navigate("/register")}
                    >
                      Create an account
                    </button>
                  </p>
                </div>
              </div>
            </div>

            <div className="order-summary">
              <h3>Order Summary</h3>

              <div className="cart-items">
                {guestItems.map((item, index) => (
                  <div key={index} className="cart-item">
                    <img
                      src={item.product?.images?.[0]?.url}
                      alt={item.product?.name}
                    />
                    <div className="item-details">
                      <h4>{item.product?.name}</h4>
                      <p>
                        Size: {item.variant?.size} | Color:{" "}
                        {item.variant?.color?.name}
                      </p>
                      <p>Qty: {item.quantity}</p>
                    </div>
                    <div className="item-price">
                      ₹{(Number(item.priceAtAdd) * item.quantity).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>

              <div className="order-totals">
                <div className="total-row">
                  <span>Subtotal:</span>
                  <span>₹{totals.subtotal.toFixed(2)}</span>
                </div>

                {appliedPromo && (
                  <div className="total-row discount-row">
                    <span>
                      <Tag size={14} />
                      Discount ({appliedPromo.code}):
                    </span>
                    <span className="discount-amount">
                      -₹{totals.discount.toFixed(2)}
                    </span>
                  </div>
                )}

                <div className="total-row">
                  <span>Shipping:</span>
                  <span>
                    {totals.shipping === null
                      ? "Enter PIN"
                      : totals.shipping === 0
                      ? "Free"
                      : `₹${totals.shipping}`}
                  </span>
                </div>

                <div className="total-row">
                  <span>Tax (GST):</span>
                  <span>₹{totals.tax.toFixed(2)}</span>
                </div>

                <div className="total-row final-total">
                  <span>Total:</span>
                  <span>₹{totals.total.toFixed(2)}</span>
                </div>
              </div>

              <div className="delivery-notice">
                <MapPin size={16} />
                <div>
                  <strong>
                    {deliveryInfo?.state
                      ? `Delivery to ${deliveryInfo.state}`
                      : "Delivery to India"}
                  </strong>
                  <p>
                    {deliveryInfo
                      ? `Estimated: ${deliveryInfo.estimatedDays} days`
                      : "Enter PIN to see delivery time"}
                  </p>
                </div>
              </div>

              <div className="security-notice">
                <Lock size={16} />
                <span>Your information is secure and encrypted</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </BackgroundWrapper>
  );
};

export default GuestCheckout;
